import React, { useState } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  Button, Avatar, Chip, Paper, Tabs, Tab, List, ListItem,
  ListItemText, ListItemAvatar, Divider, Badge
} from '@mui/material';
import {
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  MedicalServices as MedicalIcon,
  Payment as PaymentIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const PatientPortal = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Mock data
  const mockPatient = {
    name: 'John Doe',
    email: 'john.doe@example.com',
    dssn: 'DSSN-12345',
    phone: '+231-555-0100'
  };

  const mockAppointments = [
    { id: 1, doctor: 'Dr. Smith', date: '2026-07-25', time: '10:00 AM', status: 'Confirmed' },
    { id: 2, doctor: 'Dr. Johnson', date: '2026-07-28', time: '2:30 PM', status: 'Pending' },
  ];

  return (
    <Box sx={{ bgcolor: '#0a0a0a', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="xl">
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'rgba(20,20,20,0.8)', backdropFilter: 'blur(20px)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Avatar sx={{ width: 64, height: 64, bgcolor: '#00d4ff' }}>
                <PersonIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={700} color="white">
                  {mockPatient.name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                  <Chip label={mockPatient.dssn} size="small" sx={{ borderColor: '#00d4ff' }} />
                  <Typography variant="body2" color="text.secondary">
                    {mockPatient.email}
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Chip label="Verified Patient" color="success" />
          </Box>
        </Paper>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(20,20,20,0.8)', backdropFilter: 'blur(20px)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <CalendarIcon sx={{ fontSize: 40, color: '#00d4ff' }} />
                <Typography variant="h4" fontWeight={700} color="white">
                  {mockAppointments.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upcoming Appointments
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(20,20,20,0.8)', backdropFilter: 'blur(20px)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <MedicalIcon sx={{ fontSize: 40, color: '#00d4ff' }} />
                <Typography variant="h4" fontWeight={700} color="white">
                  0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Medical Records
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(20,20,20,0.8)', backdropFilter: 'blur(20px)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <PaymentIcon sx={{ fontSize: 40, color: '#00d4ff' }} />
                <Typography variant="h4" fontWeight={700} color="white">
                  $0.00
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Outstanding Balance
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(20,20,20,0.8)', backdropFilter: 'blur(20px)' }}>
              <CardContent sx={{ textAlign: 'center' }}>
                <HistoryIcon sx={{ fontSize: 40, color: '#00d4ff' }} />
                <Typography variant="h4" fontWeight={700} color="white">
                  0
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Insurance Claims
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ bgcolor: 'rgba(20,20,20,0.8)', backdropFilter: 'blur(20px)' }}>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'rgba(255,255,255,0.1)' }}>
            <Tab label="Dashboard" icon={<PersonIcon />} iconPosition="start" sx={{ color: 'white' }} />
            <Tab label="Appointments" icon={<CalendarIcon />} iconPosition="start" sx={{ color: 'white' }} />
            <Tab label="Records" icon={<HistoryIcon />} iconPosition="start" sx={{ color: 'white' }} />
            <Tab label="Payments" icon={<PaymentIcon />} iconPosition="start" sx={{ color: 'white' }} />
            <Tab label="Settings" icon={<SettingsIcon />} iconPosition="start" sx={{ color: 'white' }} />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {tabValue === 0 && (
              <Box>
                <Typography variant="h6" color="white" gutterBottom>Dashboard</Typography>
                <List>
                  {mockAppointments.map((apt) => (
                    <ListItem key={apt.id} sx={{ border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2, mb: 1 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'rgba(0, 212, 255, 0.1)' }}>
                          <CalendarIcon sx={{ color: '#00d4ff' }} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography color="white">{apt.doctor}</Typography>}
                        secondary={`${apt.date} at ${apt.time}`}
                      />
                      <Chip label={apt.status} size="small" color={apt.status === 'Confirmed' ? 'success' : 'warning'} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {tabValue === 1 && (
              <Box>
                <Typography variant="h6" color="white" gutterBottom>My Appointments</Typography>
                <Button variant="contained" sx={{ mb: 2, background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)' }}>
                  Book New Appointment
                </Button>
                <List>
                  {mockAppointments.map((apt) => (
                    <ListItem key={apt.id} sx={{ border: '1px solid rgba(255,255,255,0.05)', borderRadius: 2, mb: 1 }}>
                      <ListItemText
                        primary={<Typography color="white" fontWeight={600}>{apt.doctor}</Typography>}
                        secondary={
                          <>
                            <Typography variant="body2" color="text.secondary">{apt.date} at {apt.time}</Typography>
                            <Chip label={apt.status} size="small" color={apt.status === 'Confirmed' ? 'success' : 'warning'} sx={{ mt: 1 }} />
                          </>
                        }
                      />
                      <Button variant="outlined" size="small" sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.2)' }}>Reschedule</Button>
                      <Button variant="outlined" size="small" color="error" sx={{ ml: 1 }}>Cancel</Button>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
            {tabValue === 2 && (
              <Box>
                <Typography variant="h6" color="white" gutterBottom>Medical Records</Typography>
                <Typography color="text.secondary">No medical records available.</Typography>
              </Box>
            )}
            {tabValue === 3 && (
              <Box>
                <Typography variant="h6" color="white" gutterBottom>Payments</Typography>
                <Typography color="text.secondary">No payment history available.</Typography>
              </Box>
            )}
            {tabValue === 4 && (
              <Box>
                <Typography variant="h6" color="white" gutterBottom>Settings</Typography>
                <Typography color="text.secondary">Profile settings coming soon.</Typography>
              </Box>
            )}
          </Box>
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'right' }}>
          <Button 
            variant="outlined" 
            color="error"
            startIcon={<LogoutIcon />}
            onClick={() => {
              localStorage.removeItem('token');
              navigate('/');
            }}
          >
            Logout
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default PatientPortal;
